<template>
  <div>
    <div class="flex flex-col sm:flex-row items-start sm:items-center mb-4">
      <div class="w-full sm:w-auto mr-auto">
        <h5 class="flex items-center"><i class="dot"></i>{{ $t("정기 위험성평가") }}</h5>
      </div>

      <div v-if="!readonly && !isPrint" class="flex justify-start mt-2 sm:mt-0">
        <vs-button color="secondary" @click="handleAddRow">{{ $t("항목추가") }}</vs-button>
        <vs-button class="ml-4" color="secondary" type="border" @click="handleRemoveRow">{{ $t("선택삭제") }}</vs-button>
      </div>
    </div>

    <div style="overflow-x: auto">
      <!-- 프린트 모드 -->
      <table v-if="isPrint">
        <colgroup>
          <col style="width: 168px; min-width: 168px" />
          <col style="width: 204px; min-width: 204px" />
          <col style="min-width: 200px" />
          <col style="width: 48px; min-width: 48px" />
          <col style="width: 48px; min-width: 48px" />
          <col style="width: 48px; min-width: 48px" />
          <col style="min-width: 200px" />
          <col style="width: 89px; min-width: 89px" />
          <col style="width: 149px; min-width: 149px" />
          <col style="width: 109px; min-width: 109px" />
          <col style="width: 149px; min-width: 149px" />
        </colgroup>
        <thead>
          <tr>
            <th rowspan="2">
              <div class="w-full text-center">{{ $t("유해·위험분류") }}</div>
            </th>
            <th rowspan="2">
              <div class="w-full text-center">{{ $t("유해·위험 세부분류") }}</div>
            </th>
            <th rowspan="2">
              <div class="w-full text-center">{{ $t("유해위험요인") }}</div>
            </th>
            <th colspan="3" class="text-center">{{ $t("위험성수준") }}</th>
            <th rowspan="2">
              <div class="w-full text-center">{{ $t("위험성 감소대책") }}</div>
            </th>
            <th rowspan="2">
              <div class="w-full text-center">{{ $t("조치여부") }}</div>
            </th>
            <th rowspan="2">
              <div class="w-full text-center">{{ $t("조치완료일") }}</div>
            </th>
            <th rowspan="2">
              <div class="w-full text-center">{{ $t("조치 후") }}<br />{{ $t("위험성수준") }}</div>
            </th>
            <th rowspan="2">
              <div class="w-full text-center">{{ $t("담당자명 / 연락처") }}</div>
            </th>
          </tr>
          <tr>
            <th class="text-center">{{ $t("상") }}</th>
            <th class="text-center">{{ $t("중") }}</th>
            <th class="text-center">{{ $t("하") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in list" :key="item.id">
            <td>
              <div class="print-input">{{ $t(item.input1) }}</div>
            </td>
            <td>
              <div class="print-input">{{ item.input2 }}</div>
            </td>
            <td>
              <div class="print-textarea">{{ item.input3 }}</div>
            </td>
            <td><PrintRadio :value="item.input4 === '상'" class="justify-center" /></td>
            <td><PrintRadio :value="item.input4 === '중'" class="justify-center" /></td>
            <td><PrintRadio :value="item.input4 === '하'" class="justify-center" /></td>
            <td>
              <div class="print-textarea">{{ item.input5 }}</div>
            </td>
            <td>
              <PrintCheckbox :label="$t('완료')" :value="item.input6" style="width: fit-content" class="mx-auto" />
            </td>
            <td>
              <div class="print-input">{{ item.input7 }}</div>
            </td>
            <td>
              <div class="print-input">{{ $t(item.input8) }}</div>
            </td>
            <td>
              <div class="print-input">{{ item.input9 }}</div>
              <div class="print-input">{{ item.input10 }}</div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- 편집 모드 -->
      <table v-else>
        <colgroup>
          <col v-if="!readonly" width="36" />
          <col style="width: 168px; min-width: 168px" />
          <col style="width: 204px; min-width: 204px" />
          <col style="min-width: 200px" />
          <col style="width: 48px; min-width: 48px" />
          <col style="width: 48px; min-width: 48px" />
          <col style="width: 48px; min-width: 48px" />
          <col style="min-width: 200px" />
          <col style="width: 89px; min-width: 89px" />
          <col style="width: 149px; min-width: 149px" />
          <col style="width: 109px; min-width: 109px" />
          <col style="width: 149px; min-width: 149px" />
        </colgroup>
        <thead>
          <tr>
            <th v-if="!readonly" rowspan="2">
              <vs-checkbox :value="checkData.length === list.length && 0 < list.length" class="justify-center mx-0" @click.native="onClickAllCheck" />
            </th>
            <th rowspan="2">
              <div class="w-full text-center">{{ $t("유해·위험분류") }}</div>
            </th>
            <th rowspan="2">
              <div class="w-full text-center">{{ $t("유해·위험 세부분류") }}</div>
            </th>
            <th rowspan="2">
              <div class="w-full text-center">{{ $t("유해위험요인") }}</div>
            </th>
            <th colspan="3" class="text-center">
              <div class="flex items-center justify-center">
                {{ $t("위험성수준") }}
                <vx-tooltip :html="$t('위험성수준 상, 중의 경우만 조치 완료에 대한 내용 작성')" style="margin-left: 4px; display: flex; align-items: center; justify-content: center" position="top" delay=".1s">
                  <feather-icon icon="AlertCircleIcon" style="cursor: pointer; width: 16px" @click="onClickToolTip1()" />
                </vx-tooltip>
              </div>
            </th>
            <th rowspan="2">
              <div class="w-full text-center">{{ $t("위험성 감소대책") }}</div>
            </th>
            <th rowspan="2">
              <div class="flex items-center justify-center">
                {{ $t("조치여부") }}
                <vx-tooltip :html="$t('정기 위험성평가 당시 작성한 위험성감소대책에 대한 조치 완료여부 기입')" style="margin-left: 4px; display: flex; align-items: center; justify-content: center" position="top" delay=".1s">
                  <feather-icon icon="AlertCircleIcon" style="cursor: pointer; width: 16px" @click="onClickToolTip1()" />
                </vx-tooltip>
              </div>
            </th>
            <th rowspan="2">
              <div class="flex items-center justify-center">
                {{ $t("조치완료일") }}
                <vx-tooltip :html="$t('조치완료일은 위험성평가의 실시기간 내 기입')" style="margin-left: 4px; display: flex; align-items: center; justify-content: center" position="top" delay=".1s">
                  <feather-icon icon="AlertCircleIcon" style="cursor: pointer; width: 16px" @click="onClickToolTip1()" />
                </vx-tooltip>
              </div>
            </th>
            <th rowspan="2">
              <div class="flex items-center justify-center">
                <span> {{ $t("조치 후") }}<br />{{ $t("위험성수준") }} </span>
                <vx-tooltip :html="$t('위험성감소대책의 조치 후 위험성수준 적정보완으로 기입')" style="margin-left: 4px; display: flex; align-items: center; justify-content: center" position="top" delay=".1s">
                  <feather-icon icon="AlertCircleIcon" style="cursor: pointer; width: 16px" @click="onClickToolTip1()" />
                </vx-tooltip>
              </div>

              <div class="w-full text-center"></div>
            </th>
            <th rowspan="2">
              <div class="flex items-center justify-center">
                {{ $t("담당자명 / 연락처") }}
                <vx-tooltip :html="$t('감소대책 실행 및 모니터링 담당자명 기입<br/>* 위험성평가 담당자와 별도로 지정 가능')" style="margin-left: 4px; display: flex; align-items: center; justify-content: center" position="top" delay=".1s">
                  <feather-icon icon="AlertCircleIcon" style="cursor: pointer; width: 16px" @click="onClickToolTip1()" />
                </vx-tooltip>
              </div>
            </th>
          </tr>
          <tr>
            <th class="text-center">{{ $t("상") }}</th>
            <th class="text-center">{{ $t("중") }}</th>
            <th class="text-center">{{ $t("하") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, rowIndex) in list" :key="item.id">
            <td v-if="!readonly" style="background: #f9f9f9"><vs-checkbox v-model="checkData" :vs-value="item.id" /></td>
            <td>
              <check-type-a-selector v-model="item.input1" :readonly="readonly" class="w-full type-select" @change="handleTypeInput(rowIndex)" />
            </td>
            <td>
              <vs-select class="w-full type-select" autocomplete v-model="item.input2" :disabled="readonly">
                <vs-select-item v-for="(opt, idx) in getSubCategoryOptions(item.input1)" :key="idx" :value="opt.value" :text="$t(opt.text)" />
              </vs-select>
            </td>
            <td>
              <ml-textarea :readonly="readonly" rows="3" v-model="item.input3" class="w-full mb-0" />
            </td>
            <td>
              <PrintRadio v-if="readonly" :value="item.input4 === '상'" class="justify-center" />
              <vs-radio v-else :vs-name="'risk_' + item.id" vs-value="상" v-model="item.input4" />
            </td>
            <td>
              <PrintRadio v-if="readonly" :value="item.input4 === '중'" class="justify-center" />
              <vs-radio v-else :vs-name="'risk_' + item.id" vs-value="중" v-model="item.input4" />
            </td>
            <td>
              <PrintRadio v-if="readonly" :value="item.input4 === '하'" class="justify-center" />
              <vs-radio v-else :vs-name="'risk_' + item.id" vs-value="하" v-model="item.input4" />
            </td>
            <td>
              <ml-textarea :readonly="readonly" rows="3" v-model="item.input5" class="w-full mb-0" />
            </td>
            <td class="text-center">
              <PrintCheckbox v-if="readonly" :label="$t('완료')" :value="item.input6" style="width: fit-content" class="mx-auto" />
              <vs-checkbox v-else v-model="item.input6">{{ $t("완료") }}</vs-checkbox>
            </td>
            <td>
              <date-selector :readonly="readonly" v-model="item.input7" class="w-full" />
            </td>
            <td>
              <div v-if="readonly" class="print-input">{{ $t(item.input8) }}</div>
              <vs-select v-else class="w-full type-select" autocomplete v-model="item.input8" :disabled="readonly">
                <vs-select-item value="적정" :text="$t('적정')" />
                <vs-select-item value="보완" :text="$t('보완')" />
              </vs-select>
            </td>
            <td>
              <ml-input :readonly="readonly" v-model="item.input9" class="w-full mb-2" :placeholder="$t('홍길동')" />
              <ml-input :readonly="readonly" v-model="item.input10" class="w-full" :placeholder="$t('042-000-0000')" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import CheckTypeASelector from "@/components/selector/CheckTypeASelector";
import DateSelector from "@/components/selector/DateSelector";
import PrintRadio from "@/components/print/PrintRadio.vue";
import PrintCheckbox from "@/components/print/PrintCheckbox.vue";

export default {
  components: {
    CheckTypeASelector,
    DateSelector,
    PrintRadio,
    PrintCheckbox,
  },
  props: {
    readonly: { type: Boolean, default: false },
    isPrint: { type: Boolean, default: false },
    value: { type: Array, default: () => [] },
  },
  data() {
    return {
      checkData: [],
      list: this.value,
      numberId: 0 < this.value.length ? this.value[this.value.length - 1].id : 0,
    };
  },
  watch: {
    value: {
      handler(value) {
        this.list = value;
        this.numberId = 0 < value.length ? value[value.length - 1].id : 0;
      },
    },
    list: {
      immediate: true,
      deep: true,
      handler(value) {
        this.$emit("input", value);
      },
    },
  },
  methods: {
    onClickAllCheck() {
      if (this.checkData.length === this.list.length) this.checkData = [];
      else this.checkData = this.list.map((item) => item.id);
    },
    handleAddRow() {
      this.list.push({
        id: ++this.numberId,
        input1: "",
        input2: "",
        input3: "",
        input4: "",
        input5: "",
        input6: false,
        input7: "",
        input8: "",
        input9: "",
        input10: "",
      });
    },
    handleRemoveRow() {
      this.list = this.list.filter((item) => !this.checkData.includes(item.id));
      this.checkData = [];
    },
    handleTypeInput(index) {
      this.list[index].input2 = "";
    },
    getSubCategoryOptions(type) {
      if (type === "기계(설비)적 요인") {
        return [
          { text: "1.1 끼임(감김)", value: "1.1 끼임(감김)" },
          { text: "1.2 위험한 표면(절단, 베임, 긁힘)", value: "1.2 위험한 표면(절단, 베임, 긁힘)" },
          { text: "1.3 기계(설비)의 낙하, 비래, 전복, 붕괴, 전도 위험 부분", value: "1.3 기계(설비)의 낙하, 비래, 전복, 붕괴, 전도 위험 부분" },
          { text: "1.4 충돌위험 부분", value: "1.4 충돌위험 부분" },
          { text: "1.5 넘어짐(미끄러짐, 걸림, 헛디딤)", value: "1.5 넘어짐(미끄러짐, 걸림, 헛디딤)" },
          { text: "1.6 추락위험 부분(개구부 등)", value: "1.6 추락위험 부분(개구부 등)" },
          { text: "1.7 기타", value: "1.7 기타" },
        ];
      } else if (type === "전기적 요인") {
        return [
          { text: "2.1 감전(안전전압초과)", value: "2.1 감전(안전전압초과)" },
          { text: "2.2 아크", value: "2.2 아크" },
          { text: "2.3 정전기", value: "2.3 정전기" },
          { text: "2.4 화재 / 폭발 위험", value: "2.4 화재 / 폭발 위험" },
          { text: "2.5 기타", value: "2.5 기타" },
        ];
      } else if (type === "화학(물질)적 요인") {
        return [
          { text: "3.1 가스", value: "3.1 가스" },
          { text: "3.2 증기", value: "3.2 증기" },
          { text: "3.3 에어로졸·흄", value: "3.3 에어로졸·흄" },
          { text: "3.4 액체·미스트", value: "3.4 액체·미스트" },
          { text: "3.5 고체(분진)", value: "3.5 고체(분진)" },
          { text: "3.6 반응성 물질", value: "3.6 반응성 물질" },
          { text: "3.7 방사선", value: "3.7 방사선" },
          { text: "3.8 화재 / 폭발 위험", value: "3.8 화재 / 폭발 위험" },
          { text: "3.9 복사열 / 폭발과압", value: "3.9 복사열 / 폭발과압" },
          { text: "3.10 기타", value: "3.10 기타" },
        ];
      } else if (type === "생물학적 요인") {
        return [
          { text: "4.1 병원성 미생물, 바이러스에 의한 감염", value: "4.1 병원성 미생물, 바이러스에 의한 감염" },
          { text: "4.2 유전자 변형물질(GMO)", value: "4.2 유전자 변형물질(GMO)" },
          { text: "4.3 알러지 및 미생물", value: "4.3 알러지 및 미생물" },
          { text: "4.4 동물", value: "4.4 동물" },
          { text: "4.5 식물", value: "4.5 식물" },
          { text: "4.6 기타", value: "4.6 기타" },
        ];
      } else if (type === "작업특성 요인") {
        return [
          { text: "5.1 소음", value: "5.1 소음" },
          { text: "5.2 초음파·초저주파음", value: "5.2 초음파·초저주파음" },
          { text: "5.3 진동", value: "5.3 진동" },
          { text: "5.4 근로자 실수(휴먼에러)", value: "5.4 근로자 실수(휴먼에러)" },
          { text: "5.5 저압 또는 고압상태", value: "5.5 저압 또는 고압상태" },
          { text: "5.6 질식위험·산소결핍", value: "5.6 질식위험·산소결핍" },
          { text: "5.7 중량물 취급작업", value: "5.7 중량물 취급작업" },
          { text: "5.8 반복작업", value: "5.8 반복작업" },
          { text: "5.9 불안정한 작업자세", value: "5.9 불안정한 작업자세" },
          { text: "5.10 작업(조작)도구", value: "5.10 작업(조작)도구" },
          { text: "5.11 기후 / 고온 / 한랭", value: "5.11 기후 / 고온 / 한랭" },
          { text: "5.12 기타", value: "5.12 기타" },
        ];
      } else if (type === "작업환경 요인") {
        return [
          { text: "6.1 기후 / 고온 / 한랭", value: "6.1 기후 / 고온 / 한랭" },
          { text: "6.2 조명", value: "6.2 조명" },
          { text: "6.3 공간 및 이동통로", value: "6.3 공간 및 이동통로" },
          { text: "6.4 주변 근로자", value: "6.4 주변 근로자" },
          { text: "6.5 작업시간", value: "6.5 작업시간" },
          { text: "6.6 조직 안전문화", value: "6.6 조직 안전문화" },
          { text: "6.7 화상", value: "6.7 화상" },
          { text: "6.8 작업(조작) 도구", value: "6.8 작업(조작) 도구" },
          { text: "6.9 기타", value: "6.9 기타" },
        ];
      }
      return [];
    },
  },
};
</script>

<style lang="scss" scoped>
table {
  table-layout: fixed;
  border-collapse: collapse;
  width: 100%;
  th,
  td {
    border: 1px solid #cdcdcd;
    padding: 5px;

    &::v-deep .con-vs-checkbox {
      justify-content: center !important;
      margin: 0;
      span {
        margin: 0;
      }
    }
    &::v-deep .con-vs-radio {
      width: 100%;
      justify-content: center !important;
    }
  }
  th {
    font-size: 14px;
    font-weight: 700;
    line-height: 18px;
    text-align: center;

    background: #f9f9f9;
    padding: 7px;
  }
}

.type-select::v-deep {
  .con-select {
    width: 100%;
  }
}
</style>
